import { useEffect, useState, useCallback, useRef } from 'react';
import { fetchBoard, fetchPopularStations, fetchDisruptions, searchStations, isMissingTokenError } from '../api/client';
import { generateBoard, SERVICE_INFO } from '../data/mock';

/**
 * Resolve a station ID from a station name by querying popular list,
 * then API search if needed.
 */
export const useStationResolver = (stationName) => {
  const [stopAreaId, setStopAreaId] = useState(null);
  const [resolving, setResolving] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!stationName) {
      setStopAreaId(null);
      setResolving(false);
      return;
    }
    setResolving(true);
    (async () => {
      try {
        const popular = await fetchPopularStations();
        const lc = stationName.toLowerCase();
        const match = popular.find((s) => s.name.toLowerCase() === lc)
          || popular.find((s) => s.name.toLowerCase().startsWith(lc));
        if (match) {
          if (alive) setStopAreaId(match.id);
          return;
        }
        const results = await searchStations(stationName, 5);
        const best = results.find((s) => s.name.toLowerCase() === lc)
          || results.find((s) => s.name.toLowerCase().startsWith(lc))
          || results[0];
        if (alive) setStopAreaId(best ? best.id : null);
      } catch {
        if (alive) setStopAreaId(null);
      } finally {
        if (alive) setResolving(false);
      }
    })();
    return () => { alive = false; };
  }, [stationName]);

  return { stopAreaId, resolving };
};

/**
 * Live board hook with STALE-WHILE-REVALIDATE behaviour:
 * - First success → keep "live" status forever (no demo fallback)
 * - On subsequent error → keep showing previous data, surface a small
 *   transient warning ("reconnecting") instead of switching to mock.
 */
export const useLiveBoard = (stopAreaId, stationName) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mockFallback, setMockFallback] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const haveLiveData = useRef(false);

  const load = useCallback(async () => {
    // First time loading? show spinner. Otherwise keep current data.
    if (!haveLiveData.current) {
      setLoading(true);
      setMockFallback(false);
      setError(null);
    } else {
      setReconnecting(true);
    }

    try {
      if (!stopAreaId) {
        // Genuinely no ID → mock (only first time)
        if (!haveLiveData.current) {
          setData(generateBoard(stationName));
          setMockFallback(true);
        }
        return;
      }

      const fetchOnce = (id) => Promise.all([
        fetchBoard(id, 'departures', 20, stationName),
        fetchBoard(id, 'arrivals', 15, stationName),
      ]);

      let dep, arr;
      try {
        [dep, arr] = await fetchOnce(stopAreaId);
      } catch (firstErr) {
        const status = firstErr?.response?.status;
        if ((status === 404 || status >= 500) && stationName) {
          // Re-resolve via search and retry once
          const list = await searchStations(stationName, 1);
          if (list && list[0] && list[0].id && list[0].id !== stopAreaId) {
            [dep, arr] = await fetchOnce(list[0].id);
          } else {
            throw firstErr;
          }
        } else {
          throw firstErr;
        }
      }

      setData({
        station: dep.station || stationName,
        updated: dep.updated,
        weather: { temp: 12, humidity: 60, wind: 10 },
        departures: dep.departures || [],
        arrivals: arr.arrivals || [],
        // Keep disruptions split per direction so the banner only shows
        // the ones that are actually relevant to the active tab.
        departuresDisruptions: dep.disruptions || [],
        arrivalsDisruptions: arr.disruptions || [],
      });
      haveLiveData.current = true;
      setMockFallback(false);
      setError(null);
    } catch (err) {
      // CRITICAL: if we already had live data, KEEP IT — don't go back to mock.
      if (haveLiveData.current) {
        setError('Reconnexion en cours…');
        // Do NOT touch `data`; do NOT enable mockFallback
      } else {
        // First load and it failed → mock
        setData(generateBoard(stationName));
        setMockFallback(true);
        if (isMissingTokenError(err)) {
          setError('Mode démo : token Navitia non configuré côté backend.');
        } else if (err?.response?.status === 429) {
          setError('Quota Navitia journalier atteint — données indisponibles temporairement. Réessayez dans quelques minutes.');
        } else {
          setError(err?.response?.data?.detail || err.message || 'Erreur de chargement');
        }
      }
    } finally {
      setLoading(false);
      setReconnecting(false);
    }
  }, [stopAreaId, stationName]);

  // Reset live-data flag when station changes
  useEffect(() => {
    haveLiveData.current = false;
  }, [stopAreaId, stationName]);

  useEffect(() => { load(); }, [load]);

  return { data, loading, error, mockFallback, reconnecting, reload: load };
};

export const useDisruptions = (stopAreaId) => {
  const [items, setItems] = useState([]);
  const [mockFallback, setMockFallback] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const list = await fetchDisruptions(stopAreaId);
        if (!alive) return;
        if (!list || list.length === 0) {
          setItems([]);
          setMockFallback(false);
        } else {
          setItems(list);
          setMockFallback(false);
        }
      } catch {
        if (!alive) return;
        setItems(SERVICE_INFO);
        setMockFallback(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [stopAreaId]);

  return { items, mockFallback, loading };
};
